<template>
  <div class="tag-chapters-tree">
    <div v-if="isLoading" class="p-4 text-center text-grey">
      <b-icon pack="fas" icon="spinner" class="fa-pulse" />
      {{ $t('loading') }}
    </div>

    <template v-else>
      <!-- Badges resumen + importar cantidad ejecutada -->
      <div class="flex items-center justify-between flex-wrap mb-3">
        <div class="flex items-center flex-wrap">
          <span class="summary-badge bg-blue-light text-blue-darker mr-2 mb-2">
            <b-icon pack="fas" icon="layer-group" size="is-small" class="mr-1" />
            {{ summary.disciplinas }} {{ $t('disciplines') }}
          </span>
          <span class="summary-badge bg-green-light text-green-darker mr-2 mb-2">
            <b-icon pack="fas" icon="file-alt" size="is-small" class="mr-1" />
            {{ summary.pestanas }} {{ $t('sheets_count_label') }}
          </span>
          <span class="summary-badge bg-orange-light text-orange-darker mr-2 mb-2">
            <b-icon pack="fas" icon="users-cog" size="is-small" class="mr-1" />
            {{ summary.equipos }} {{ $t('equipment_count_label') }}
          </span>
          <span class="summary-badge bg-cyan-light text-cyan-darker mr-2 mb-2">
            <b-icon pack="fas" icon="link" size="is-small" class="mr-1" />
            {{ summary.recursos }} {{ $t('resources') }}
          </span>
        </div>
        <v-button @click="showImportExecutedQuantity = true" size="small" class="mb-2">
          <b-icon icon="file-import" pack="fas" />
          {{ $t('import_executed_quantity') }}
        </v-button>
      </div>

      <div class="flex w-full">
        <div :class="showImportExecutedQuantity ? 'w-3/4 pr-2' : 'w-full'">
          <div v-if="!chapters.length" class="p-4 text-center text-grey panel">
            {{ $t('no_results') }}
          </div>

          <!-- Arbol de capitulos (Disciplina / Pestaña / Equipo) -->
          <table v-if="chapters.length" class="chapter-tree">
        <thead>
          <tr>
            <th class="col-structure">{{ $t('structure_identification') }}</th>
            <th class="col-level">{{ $t('level') }}</th>
            <th class="col-actions">{{ $t('actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in chapterRows">
            <tr :key="'ch_' + row.id"
              :class="['tree-row', 'level-' + row.level]"
              @click="toggleNode(row.id)">
              <td class="col-structure">
                <div class="flex items-center" :style="{ paddingLeft: ((row.depth) * 22) + 'px' }">
                  <span class="expand-toggle">
                    <b-icon pack="fas" size="is-small" :icon="isExpanded(row.id) ? 'caret-down' : 'caret-right'" />
                  </span>
                  <b-icon pack="fas" :icon="iconForLevel(row.level)" size="is-small" class="mr-2" :class="iconClassForLevel(row.level)" />
                  <span class="node-name">{{ row.name }}</span>
                  <span v-if="row.assignments_total > 0" class="ml-2 text-xs text-grey-dark">({{ row.assignments_total }})</span>
                </div>
              </td>
              <td class="col-level">
                <span class="level-badge" :class="'level-badge--' + row.level">
                  <span class="level-circle">{{ row.level }}</span>
                  {{ labelForLevel(row.level) }}
                </span>
              </td>
              <td class="col-actions">
                <!-- Exportar a cualquier nivel: el backend ya resuelve descendientes via collectChapterAndDescendantIds. -->
                <b-button size="is-small" type="is-success" icon-left="file-export" icon-pack="fas"
                  :loading="exportingChapterId === row.id" @click.stop="exportChapter(row)">
                  {{ $t('export') }}
                </b-button>
              </td>
            </tr>

            <!-- Cuando el capitulo se expande por primera vez, montamos <measurement> con filtro por chapter
                 y NO lo desmontamos nunca (v-show controla visibilidad). Asi:
                 - Solo 1 llamada por capitulo (la primera apertura).
                 - Colapsar/re-expandir es instantaneo, sin re-fetch del endpoint pesado /assignments. -->
            <tr v-if="wasEverExpanded(row.id) && row.assignments_count > 0"
              v-show="isExpanded(row.id)"
              :key="'meas_' + row.id"
              class="nested-row">
              <td colspan="3" class="nested-cell">
                <div :style="{ paddingLeft: ((row.depth + 1) * 22) + 'px' }" class="pt-2 pb-3 pr-2">
                  <measurement
                    :key="'meas_comp_' + row.id + '_' + measurementsRefreshKey"
                    type="resource"
                    :chapter-id="row.id"
                    :depth="0" />
                </div>
              </td>
            </tr>

            <!-- Mensaje vacio -->
            <tr v-if="isExpanded(row.id) && row.assignments_count === 0 && !row.hasChildren" :key="'empty_' + row.id" class="empty-row-tr">
              <td colspan="3">
                <div :style="{ paddingLeft: ((row.depth + 1) * 22) + 'px' }" class="text-grey-dark text-sm py-1">
                  {{ emptyMessageForChapter(row) }}
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
        </div>

        <div v-if="showImportExecutedQuantity" class="w-1/4 min-w-0">
          <div class="panel p-2 my-4 import-panel">
            <div class="flex items-center justify-between w-full mb-5">
              <div>
                <label><b>{{ $t('import_executed_quantity') }}</b></label>
              </div>
              <div>
                <b-tooltip :label="$t('close')" position="is-left">
                  <div
                    class="flex items-center justify-center w-8 h-8 rounded-full cursor-pointer bg-red text-grey-lightest"
                    @click="closeImport()">
                    <b-icon pack="fas" icon="times" size="is-small" />
                  </div>
                </b-tooltip>
              </div>
            </div>
            <div class="w-full pb-3">
              <import-entities ref="importEntities" :can-overwrite="false" :project-id="project ? project.id : 0" :with-history="true"
                entity-type="assignments-executedquantity" @success="onImportSuccess" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import measurement from '../../pages/project/measurement.vue';
import ImportEntities from '../import-entities';

export default {
  name: 'tag-chapters-tree',

  components: { measurement, ImportEntities },

  props: {
    project: { type: Object, required: true },
  },

  data() {
    return {
      isLoading: false,
      chapters: [],
      expandedIds: {},
      everExpandedIds: {},
      exportingChapterId: null,
      showImportExecutedQuantity: false,
      measurementsRefreshKey: 0,
    };
  },

  computed: {
    ...mapGetters({
      user: 'auth/user',
    }),

    summary() {
      const s = { disciplinas: 0, pestanas: 0, equipos: 0, recursos: 0 };
      const walk = (nodes) => {
        for (const n of nodes) {
          if (n.level === 1) s.disciplinas += 1;
          if (n.level === 2) s.pestanas += 1;
          if (n.level === 3) s.equipos += 1;
          s.recursos += n.assignments_count || 0;
          if (Array.isArray(n.children) && n.children.length) walk(n.children);
        }
      };
      walk(this.chapters);
      return s;
    },

    chapterRows() {
      const out = [];
      const walk = (nodes, depth) => {
        for (const n of nodes) {
          const hasChildren = Array.isArray(n.children) && n.children.length > 0;
          out.push({
            id: n.id, name: n.name, code: n.code, level: n.level, depth,
            hasChildren,
            assignments_count: n.assignments_count || 0,
            assignments_total: n.assignments_total || 0,
          });
          if (this.expandedIds[n.id] && hasChildren) walk(n.children, depth + 1);
        }
      };
      walk(this.chapters, 0);
      return out;
    },
  },

  mounted() { this.loadTree(); },

  methods: {
    async loadTree({ silent = false } = {}) {
      if (!silent) this.isLoading = true;
      try {
        const { data } = await axios.get(`/api/v2/project/${this.project.id}/tag-chapters/tree`);
        if (data && data.success) {
          this.chapters = data.chapters || [];
        } else { this.$notify.error('error_loading_data'); }
      } catch (e) { this.$notify.error('error_loading_data'); }
      finally {
        if (!silent) this.isLoading = false;
      }
    },

    toggleNode(id) {
      const opening = !this.expandedIds[id];
      this.$set(this.expandedIds, id, opening);
      if (opening && !this.everExpandedIds[id]) {
        this.$set(this.everExpandedIds, id, true);
      }
    },
    isExpanded(id) { return !!this.expandedIds[id]; },
    wasEverExpanded(id) { return !!this.everExpandedIds[id]; },

    async exportChapter(row) {
      this.exportingChapterId = row.id;
      try {
        const response = await axios.get('/api/v2/export/assignment', {
          responseType: 'blob',
          params: {
            projectId: this.project.id,
            orderField: 'name',
            orderDir: 'asc',
            template: 'resources_breakdown',
            chapterId: row.id,
          },
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${this.$t('assignments')}_${this.slug(row.name)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        this.$notify.success('success_export');
      } catch (e) { this.$notify.error('error_export'); }
      finally { this.exportingChapterId = null; }
    },

    emptyMessageForChapter(/* row */) {
      return this.$t('no_results');
    },

    iconForLevel(level) { return level === 1 ? 'layer-group' : level === 2 ? 'file-alt' : 'users-cog'; },
    iconClassForLevel(level) { return level === 1 ? 'text-blue' : level === 2 ? 'text-green' : 'text-orange'; },
    labelForLevel(level) {
      if (level === 1) return this.$t('discipline');
      if (level === 2) return this.$t('tag_chapters_level_2');
      if (level === 3) return this.$t('tag_chapters_level_3');
      return this.$t('level_n', [level]);
    },

    slug(s) { return (s || 'chapter').toString().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''); },

    onImportSuccess() {
      if (
        this.$refs.importEntities &&
        Array.isArray(this.$refs.importEntities.warnings) &&
        this.$refs.importEntities.warnings.length === 0
      ) {
        this.showImportExecutedQuantity = false;
      }
      this.measurementsRefreshKey += 1;
      this.loadTree({ silent: true });
    },

    closeImport() {
      this.showImportExecutedQuantity = false;
    },
  },
};
</script>

<style scoped>
.tag-chapters-tree .summary-badge { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 14px; font-size: 13px; font-weight: 600; }
.tag-chapters-tree .bg-blue-light { background: #dbeafe; }
.tag-chapters-tree .bg-green-light { background: #d1f3df; }
.tag-chapters-tree .bg-orange-light { background: #ffe6c2; }
.tag-chapters-tree .bg-cyan-light { background: #ccf2f1; }
.tag-chapters-tree .text-blue-darker { color: #1d4ed8; }
.tag-chapters-tree .text-green-darker { color: #166534; }
.tag-chapters-tree .text-orange-darker { color: #9a3412; }
.tag-chapters-tree .text-cyan-darker { color: #115e59; }
.tag-chapters-tree .text-grey-darker { color: #333; }
.tag-chapters-tree .text-blue { color: #3273dc; }
.tag-chapters-tree .text-green { color: #22863a; }
.tag-chapters-tree .text-orange { color: #d97706; }
.tag-chapters-tree .text-grey-dark { color: #666; }

.tag-chapters-tree .chapter-tree {
  width: 100%; border-collapse: collapse; background: #fff;
  border: 1px solid #e0e0e0; border-radius: 4px; overflow: hidden;
}
.tag-chapters-tree .chapter-tree thead th {
  background: #5577BB; color: #fff; text-align: left; font-weight: 600;
  padding: 10px 12px; font-size: 13px; vertical-align: middle;
}
.tag-chapters-tree .chapter-tree tbody tr { border-top: 1px solid #eee; cursor: pointer; }
.tag-chapters-tree .chapter-tree tbody tr:hover { background: #f7fafc; }
.tag-chapters-tree .tree-row td { padding: 8px 12px; font-size: 13px; vertical-align: middle; }
.tag-chapters-tree .tree-row.level-1 td { background: #f7faff; font-weight: 600; }
.tag-chapters-tree .tree-row.level-1:hover td { background: #eef3fb; }
.tag-chapters-tree .empty-row-tr td { padding: 4px 12px; background: #fafafa; cursor: default; }
.tag-chapters-tree .nested-row td.nested-cell { background: #f9fbff; padding: 8px 12px; cursor: default; }

.tag-chapters-tree .col-level { width: 180px; }
.tag-chapters-tree .col-actions { width: 130px; text-align: right; }

.tag-chapters-tree .expand-toggle { display: inline-flex; width: 18px; align-items: center; cursor: pointer; }

.tag-chapters-tree .level-badge {
  display: inline-flex; align-items: center; padding: 2px 10px 2px 4px;
  border-radius: 12px; font-size: 12px; font-weight: 600;
}
.tag-chapters-tree .level-circle {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 50%; color: #fff; font-weight: 700;
  margin-right: 6px; font-size: 11px;
}
.tag-chapters-tree .level-badge--1 { background: #dbeafe; color: #1d4ed8; }
.tag-chapters-tree .level-badge--1 .level-circle { background: #2563eb; }
.tag-chapters-tree .level-badge--2 { background: #d1f3df; color: #166534; }
.tag-chapters-tree .level-badge--2 .level-circle { background: #16a34a; }
.tag-chapters-tree .level-badge--3 { background: #ffe6c2; color: #9a3412; }
.tag-chapters-tree .level-badge--3 .level-circle { background: #ea580c; }

.tag-chapters-tree .import-panel { overflow-x: auto; }
</style>
